import {repositoryURL, type Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import type {MessageAttachment, MessageAttachmentKind} from '@microsoft/agent-host-protocol'

import type {TrackedAttachment} from './process-uploaded-files'

export interface RepositoryFileSelection {
  repo: Repository
  path: string
  type: 'file' | 'directory'
  ref?: string
  commitOID?: string
}

export function repositoryFileAttachmentKey(attachment: MessageAttachment): string | undefined {
  const detail = attachment._meta?.['copilot.attachmentDetail']
  if (!detail || typeof detail !== 'object' || !('type' in detail) || detail.type !== 'github_file') return
  if (!('repo' in detail) || !detail.repo || typeof detail.repo !== 'object') return
  const repo = detail.repo
  if (!('owner' in repo) || typeof repo.owner !== 'string' || !('name' in repo) || typeof repo.name !== 'string') return
  if (!('ref' in detail) || typeof detail.ref !== 'string' || !('path' in detail) || typeof detail.path !== 'string')
    return
  return JSON.stringify([repo.owner.toLowerCase(), repo.name.toLowerCase(), detail.ref, detail.path])
}

export function createRepositoryFileAttachment(
  selection: RepositoryFileSelection,
  prefix = 'repository-file:',
): TrackedAttachment {
  const [owner = '', ...name] = selection.repo.name.split('/')
  const ref = selection.ref || selection.commitOID || 'HEAD'
  // The SDK resolves `ref`, so pin the preview's commit there and keep the
  // selected branch separately in UI metadata when the branch later advances.
  const revision = selection.commitOID || ref
  const path = selection.path.split('/').map(encodeURIComponent).join('/')
  const kind = selection.type === 'directory' ? 'tree' : 'blob'
  const url = `${repositoryURL(selection.repo)}/${kind}/${encodeURIComponent(revision)}/${path}`
  return {
    token: {
      id: `${prefix}${selection.repo.name}:${revision}:${selection.path}`,
      label: selection.path,
      icon: selection.type === 'directory' ? 'folder' : 'file',
      url,
    },
    ahp: {
      type: 'simple' as MessageAttachmentKind.Simple,
      label: selection.path,
      displayKind: selection.type === 'directory' ? 'directory' : 'document',
      _meta: {
        'copilot.attachmentDetail': {
          type: 'github_file',
          repo: {owner, name: name.join('/'), id: selection.repo.id},
          ref: revision,
          path: selection.path,
          url,
        },
        'github.attachmentUi': {
          ref,
          ...(selection.commitOID ? {commitOID: selection.commitOID} : {}),
          private: selection.repo.private,
        },
      },
    },
  }
}
